import React from 'react';
import { Option } from 'types/types';
import ToggleButton from 'components/v2/compiler/panels/toggleable/ToggleButton';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { DoubleFieldGrid, ExpandablePanel, ExpandablePanelHeaderName, HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { ToolForAdminModelVersion } from 'types/admin-tools/ToolForAdminModelVersion';
import getErrorsLikeFormik from './utils/getErrorsLikeFormik';
import { useUpdateToolMutation } from 'services/api/adminToolsQueriesAndMutations';
import { PatchedToolForAdminModel } from 'types/admin-tools/PatchedToolForAdminModel';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import { ToolVerifierOption } from 'types/admin-tools/ToolVerifierOption';
import WizardDateOnlyPicker from 'components/v2/epd-wizard-inputs/WizardDateOnlyPicker';
import AdminEditToolVersionButtons from './AdminEditToolVersionButtons';
import AdminEditToolVersionPcrs from './AdminEditToolVersionPcrs';
import AdminEditToolVersionToggleSection from './AdminEditToolVersionToggleSection';
import { IS_QUALITY_CHECK_VISIBLE, toolVersionSchema } from './utils/toolValidationSchemas';
import { ToolVersionState } from 'types/tools/ToolVersionState';
import { IsReadOnlyModeContext } from 'components/v2/compiler/hooks/useIsReadOnlyMode';
import AdminEditToolVersionFiles from './AdminEditToolVersionFiles';
import ToolVersionStats from '../../../../components/tools/ToolVersionStats';
import { HelpBox } from 'components/v2/help-boxes';

interface Props {
  toolData: PatchedToolForAdminModel;
  versionData: ToolForAdminModelVersion;
  hasHeadErrors: boolean;
  toolVerifiers: ToolVerifierOption[];
}

const QUALITY_INTERVALS = [5, 10, 25, 50, 100, 200, 350];
const QUALITY_INTERVAL_OPTIONS: Option[] = QUALITY_INTERVALS.map((i) => ({ value: String(i), label: String(i) }));

const AdminEditToolVersion: React.FC<Props> = ({ toolData, versionData, hasHeadErrors, toolVerifiers }) => {
  const updateTool = useUpdateToolMutation(toolData.id);

  const handlePropertyChanged = (propertyName: string, value: any) => {
    updateTool.mutate({ propertyName, value, versionOrd: versionData.ord });
  };

  const handleOptionChanged = (options: Option[]) => (propertyName: string, value: any) => {
    const newOption = options.find((o) => o.value === value);
    handlePropertyChanged(propertyName, newOption);
  };

  const versionDisplayId = versionData.ord.toString().padStart(3, '0');

  const isReadOnlyMode = versionData.versionState === ToolVersionState.ENABLED;
  const errors = getErrorsLikeFormik(toolVersionSchema, versionData);
  const hasVersionErrors = Object.keys(errors).length > 0;
  const hasErrors = hasHeadErrors || hasVersionErrors;

  return (
    <ExpandablePanel
      headerTemplate={(options) => (
        <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
          <ExpandablePanelHeaderName>
            <HeaderCaptionSemiBold>{versionDisplayId} - tool version</HeaderCaptionSemiBold>
            <ToggleButton options={options} />
          </ExpandablePanelHeaderName>

          <AdminEditToolVersionButtons toolData={toolData} versionData={versionData} />
        </div>
      )}
      toggleable
    >
      <DoubleFieldGrid>
        <IsReadOnlyModeContext.Provider value={isReadOnlyMode}>
          <WizardTextInputComponent
            label="Internal Tool Version number (EPD Internationals system)"
            tooltip="The Internal Tool Version is used by EPD International to track changes every time the tool is saved in the system. It is not visible to tool owners, verifiers or users. The Manufacturer Tool Version is the version provided by the tool owner and is what is shown publicly."
            name="ord"
            value={versionDisplayId}
            disabled
          />

          <WizardTextInputComponent
            label="Tool Version Number (Tool Owner)"
            tooltip="Enter the tool version number as defined by the tool owner (e.g., 2.0.1). This is the version that will be visible to users and is separate from the internal version number assigned by EPD International."
            placeholder="Type here"
            name="name"
            error={errors.name}
            value={versionData.name}
            maxLength={50}
            onChanged={handlePropertyChanged}
          />

          <CompilerSelectControlledComponent
            label="Tool Verifier"
            name="verifierUser"
            error={errors.verifierUser}
            options={toolVerifiers}
            value={versionData.verifierUser}
            onChanged={handleOptionChanged(toolVerifiers)}
            placeholder="Select"
            isClearable
          />

          <WizardTextInputComponent
            label="Tool Verifier Company"
            placeholder="Type here"
            name="verifierCompanyName"
            error={errors.verifierCompanyName}
            value={versionData.verifierCompanyName}
            maxLength={100}
            onChanged={handlePropertyChanged}
          />

          <WizardTextInputComponent
            key={versionData.verifierUser?.email}
            label="Tool Verifier Email"
            name="verifierUserEmail"
            value={versionData.verifierUser?.email ?? ''}
            disabled
          />

          <WizardDateOnlyPicker
            label="Tool Approval Date by Verifier"
            placeholder="Select date"
            name="verifierApprovalDate"
            defaultValue={versionData.verifierApprovalDate}
            error={errors.verifierApprovalDate}
            maxValueType="today-inclusive"
            onChanged={handlePropertyChanged}
          />

          <WizardDateOnlyPicker
            label="Tool Approval Date by EPD International"
            placeholder="Select date"
            name="adminApprovalDate"
            defaultValue={versionData.adminApprovalDate}
            error={errors.adminApprovalDate}
            maxValueType="today-inclusive"
            onChanged={handlePropertyChanged}
          />

          <WizardDateOnlyPicker
            label="Validity Date"
            placeholder="Select date"
            name="validityDate"
            defaultValue={versionData.validityDate}
            error={errors.validityDate}
            minValueType="today-inclusive"
            onChanged={handlePropertyChanged}
          />

          <AdminEditToolVersionPcrs toolData={toolData} versionData={versionData} errors={errors} />

          <ToolVersionStats variant="admin" toolData={toolData} versionData={versionData} />

          {IS_QUALITY_CHECK_VISIBLE && (
            <CompilerSelectControlledComponent
              label="Quality Check Interval"
              tooltip="Every n-th EPD will trigger an automatic quality check"
              name="qualityInterval"
              error={errors.qualityInterval}
              options={QUALITY_INTERVAL_OPTIONS}
              value={
                versionData.qualityInterval
                  ? QUALITY_INTERVAL_OPTIONS.find((o) => o.value === String(versionData.qualityInterval))
                  : null
              }
              onChanged={(propertyName, newValue) => handlePropertyChanged(propertyName, newValue ? Number(newValue) : null)}
              placeholder="Select"
              isClearable
            />
          )}

          <AdminEditToolVersionFiles toolData={toolData} versionData={versionData} errors={errors} />
        </IsReadOnlyModeContext.Provider>

        {hasErrors && (
          <HelpBox className="w-full">
            Please check: All mandatory fields must be filled in before publishing the tool version.
          </HelpBox>
        )}

        <AdminEditToolVersionToggleSection toolData={toolData} versionData={versionData} hasErrors={hasErrors} />
      </DoubleFieldGrid>
    </ExpandablePanel>
  );
};

export default AdminEditToolVersion;
